import { addDays, DateOnly, endOfDay } from '@if/core/date'
import { FormBody, FormContainerProvider, FormSetup } from '@if/mui'
import TuneIcon from '@mui/icons-material/Tune'
import { Alert, Card, CardContent, CardHeader, Divider, Grid, Stack, Typography } from '@mui/material'
import PageSpinner from '@src/components/PageSpinner'
import { useUser } from '@src/contexts/AppContext'
import {
  useBannerCurrentCustomBuydownPlan,
  useCurrentCustomBuydownPlan,
  useSaveCustomBuydownPlan,
  useSetBannerOwnership,
} from '@src/data/api/custom-buydown-api/custom-buydown-api'
import { useMerchantById } from '@src/data/api/merchants-api/merchants-api'
import { CustomBuydownPlan } from '@src/types/CustomBuydown'
import { Merchant } from '@src/types/Merchant'
import { useTranslation } from 'react-i18next'
import { OwnershipSwitch } from './components/OwnershipSwitch'
import { PlanFormFields } from './components/PlanFormFields'
import { PlanSummaryCard } from './components/PlanSummaryCard'
import { CustomBuydownFormValues, customBuydownSchema } from './customBuydown-schema'

const CUSTOM_BUYDOWN_OWNED_FIELD = 'CustomBuydown'
const DEFAULT_LENDER_ADMIN_FEE = 0.06

const ownershipFromMerchant = (merchant: Merchant | null) => ({
  ownedByBanner: merchant?.bannerOwnedFields?.includes(CUSTOM_BUYDOWN_OWNED_FIELD) ?? false,
})

const hasOwnershipChanged = (values: CustomBuydownFormValues, merchant: Merchant | null): boolean =>
  values.ownedByBanner !== ownershipFromMerchant(merchant).ownedByBanner

const defaultsFromPlan = (plan: CustomBuydownPlan | null, merchant: Merchant | null): CustomBuydownFormValues => {
  const ownership = ownershipFromMerchant(merchant)
  if (!plan)
    return {
      contribution: 0.0499,
      activeFrom: addDays(DateOnly.today(), 1),
      expiresOn: null,
      maxFinancingAmount: null,
      ...ownership,
    }

  const max = plan.promoTermRates[0]?.maxFinancingAmount
  const tomorrow = new DateOnly(addDays(DateOnly.today(), 1))
  const planActiveFrom = new DateOnly(new Date(plan.activeFrom))
  return {
    contribution: plan.merchantFeeRate,
    activeFrom: planActiveFrom.getTime() >= tomorrow.getTime() ? planActiveFrom : tomorrow,
    expiresOn: plan.expiresOn ? new DateOnly(new Date(plan.expiresOn)) : null,
    maxFinancingAmount: max && max > 0 ? max : null,
    ...ownership,
  }
}

const CustomBuydownPage = () => {
  const { t } = useTranslation()
  const user = useUser()
  const isBanner = user?.isBannerUser ?? false
  const merchantId = user?.merchantId ?? ''
  const [plan, isFetching] = useCurrentCustomBuydownPlan(merchantId)
  const [merchant] = useMerchantById(merchantId)
  const savePlan = useSaveCustomBuydownPlan(merchantId)
  const setOwnership = useSetBannerOwnership(merchantId)
  const adminFeeRate = plan?.borrowerFeeRate || merchant?.lenderAdminFee || DEFAULT_LENDER_ADMIN_FEE

  const isChild = !!merchant?.parentId
  const isLocked = isChild && (merchant?.bannerOwnedFields ?? []).includes(CUSTOM_BUYDOWN_OWNED_FIELD)
  const [bannerPlan, isFetchingBannerPlan] = useBannerCurrentCustomBuydownPlan(merchantId, isChild)
  const bannerPlanValues = isChild && !isLocked && bannerPlan ? defaultsFromPlan(bannerPlan, merchant) : null

  if (isFetching || !merchant || isFetchingBannerPlan) return <PageSpinner isLoading />

  const formSetup = (): FormSetup<CustomBuydownFormValues> => ({
    schema: customBuydownSchema(),
    mode: 'onChange',
    defaultValues: defaultsFromPlan(plan ?? bannerPlan, merchant),
  })

  const handleSubmit = async (values: CustomBuydownFormValues) => {
    const saved = await savePlan.mutateAsync({
      planKey: plan?.planKey,
      dto: {
        contribution: values.contribution,
        activeFrom: values.activeFrom.toString(),
        expiresOn: values.expiresOn ? endOfDay(values.expiresOn).toString() : undefined,
        maxFinancingAmount: values.maxFinancingAmount ?? undefined,
        versionTag: plan?.versionTag ?? undefined,
      },
    })
    if (isBanner && hasOwnershipChanged(values, merchant)) {
      await setOwnership.mutateAsync({ ownedByBanner: values.ownedByBanner })
    }
    return saved
  }

  return (
    <>
      <Stack spacing={1} sx={{ mb: 3 }}>
        <Typography variant="h5">{t('customBuydown.title')}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t('customBuydown.subtitle')}
        </Typography>
      </Stack>
      {isLocked && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {t('customBuydown.inherited.lockedNotice')}
        </Alert>
      )}
      <FormContainerProvider<CustomBuydownFormValues, unknown, Promise<CustomBuydownPlan>>
        key={`${plan?.planKey ?? 'new'}-${bannerPlan?.planKey ?? 'none'}`}
        role={plan ? 'edit' : 'create'}
        useFormSetup={formSetup}
        onSubmit={handleSubmit}
        successMessage={(saved) => t('customBuydown.savedSuccess', { planCode: saved.label })}
      >
        {isBanner && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <OwnershipSwitch />
            </CardContent>
          </Card>
        )}
        <Grid container spacing={3} alignItems="flex-start">
          <Grid size={{ xs: 12, md: 7 }}>
            <Card>
              <CardHeader
                avatar={<TuneIcon />}
                title={t('customBuydown.reduceRateTitle')}
                subheader={isLocked ? t('customBuydown.inherited.setByBanner') : undefined}
              />
              <Divider />
              <CardContent>
                <FormBody<CustomBuydownFormValues>>
                  <PlanFormFields adminFeeRate={adminFeeRate} disabled={isLocked} bannerPlanValues={bannerPlanValues} />
                </FormBody>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <PlanSummaryCard adminFeeRate={adminFeeRate} locked={isLocked} />
          </Grid>
        </Grid>
      </FormContainerProvider>
    </>
  )
}

export default CustomBuydownPage
